<!--

ViewEditBlock.vue
============

Used to create a panel with text, that can then be edited. This expects a collection of data,
    with layout applied to each. The definition for layout and the other parameters can be found
    in ViewEdit.vue
To use this add the viewEdit/ViewEdit.vue component and call it with:

<view-edit-block v-model="model"
                 :list-type="list-type (see below)"
                 :list-class="class name" // class to apply to the list
                 :layout="layout" // the layout for the view and edit
                 :edit-mode-on="editModeOn" // true=edit mode, false=view mode (default false)
                 :show-delete-item="[true|false]" // true=put a delete button the item (default false)
                 :show-add-item="[true|false]" // true=put an add button on the item (default false)
                 :sending="sending" // true=update in progress, false=waiting (default false). Used to control an update button
                 :errorText="error" // String showing an error. Shown if non empty or null
                 :table-on-print="[true|false]" // true=show this as a table when printing
                 :printing="[true|false]" // true=show the page as if it is printing
                 @cancel-pressed="cancelPressed" // event emitted when the "Go Back" button is pressed
                 @update:modelValue="model updated" // event emitted when the "Update" button is pressed and the model is updated
                 @delete-pressed="deletePressed" /> // event emitted when the "Delete" button is pressed

* model is the data to work with. This is an array of items
* list-type is the type of list to present.
    ** null or empty = no list type. Just repeated blocks
    ** 'ordered' is a numbered list (ol)
    ** 'unordered' is a bullet list (ul)
* layout is how the data is to be presented. The object description is below
* edit-mode-on is a bool flag to indicate whether we are viewing (false) or editing (true)
* @cancel-pressed is called when the user, in edit mode, clicked cancel (Go Back)
* @update:modelValue is called when the model is updated, which happens when the user, in edit mode, clicked "Update"


-->



<template>
    <!-- Always visible View section -->
    <div :style="dispStyle"
         :class="printViewStyle">
        <template v-if="!listType">
            <div v-for="(row, index) in data"
                 :key="`model-${index}`">
                <view-edit-view :data="data[row]"
                                :layout="layout"
                                :new-id="`${layout.id}-${index}`" />
            </div>
        </template>
        <template v-else-if="listType == 'ordered'">
            <ol :class="olClass(listClass)">
                <li v-for="(row, index) in data"
                    :key="`model-${index}`"
                    class="between-item-spacing">
                    <view-edit-view :data="data[index]"
                                    :layout="layout"
                                    :new-id="`${layout.id}-${index}`" />
                </li>
            </ol>
        </template>
        <template v-else-if="listType == 'unordered'">
            <ul :class="olClass(listClass)">
                <li v-for="(row, index) in data"
                    :key="`model-${index}`"
                    class="between-item-spacing">
                    <view-edit-view :data="data[index]"
                                    :layout="layout" />
                </li>
            </ul>
        </template>
    </div>
    <div v-if="tableOnPrint"
         :class="printPrintStyle">
        <view-edit-block-table :items="data"
                               :layout="layout" />
    </div>

    <!-- Dialog based Edit section -->

    <template v-if="editModeOn">
        <div :id="dialogId"
             class="modal fade"
             role="dialog"
             aria-labelledby="editDetailsTitle"
             aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered"
                 :class="dialogSize(layout.editSize)"
                 role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <label id="editDetailsTitle"
                               class="modal-title">
                            {{ layout.editTitle }}
                        </label>
                    </div>
                    <div class="modal-body">
                        <template v-if="!listType">
                            <view-edit-edit v-for="(hdata, eindex) in heldData"
                                            :key="`edir-model-${eindex}`"
                                            :data="heldData[eindex]"
                                            :comparison="heldComparison[eindex]"
                                            :layout="layout"
                                            :index="eindex"
                                            :show-delete="showDeleteItem"
                                            @change="modelChanged"
                                            @refresh="refresh" />
                            <p class="warning-label text-end">
                                {{ errorText }}
                            </p>
                        </template>
                        <template v-if="listType == 'ordered'">
                            <ol>
                                <li v-for="(hdata, eindex) in heldData"
                                    :key="`edir-model-${eindex}`">
                                    <view-edit-edit :data="heldData[eindex]"
                                                    :comparison="heldComparison[eindex]"
                                                    :layout="layout"
                                                    :index="eindex"
                                                    :show-delete="showDeleteItem"
                                                    @change="modelChanged"
                                                    @refresh="refresh"
                                                    @delete="deleteItem" />
                                    <p class="warning-label text-end">
                                        {{ errorText }}
                                    </p>
                                </li>
                            </ol>
                        </template>
                        <template v-if="listType == 'unordered'">
                            <ul>
                                <li v-for="(hdata, eindex) in heldData"
                                    :key="`edit-model-${eindex}`">
                                    <view-edit-edit :data="heldData[eindex]"
                                                    :comparison="heldComparison[eindex]"
                                                    :layout="layout"
                                                    :index="eindex"
                                                    @change="modelChanged"
                                                    @refresh="refresh"
                                                    @delete="deleteItem" />
                                    <p class="warning-label text-end">
                                        {{ errorText }}
                                    </p>
                                </li>
                            </ul>
                        </template>
                        <div v-if="showAddItem">
                            <button type="button"
                                    class="btn outline icon-button"
                                    @click="addClicked">
                                <i class="icon icon-plus" />
                            </button>
                        </div>
                    </div>
                    <div class="modal-footer left-align">
                        <div v-if="showDelete">
                            <button type="button"
                                    class="btn outline icon-button"
                                    data-bs-toggle="tooltip"
                                    title="Delete"
                                    @click="deletePressed">
                                <i class="icon icon-delete IconAsButton" />
                            </button>
                        </div>
                        <div class="ms-auto">
                            <button type="button"
                                    class="btn btn-color btn-outline-primary btn-sm"
                                    @click="cancelEdit">
                                Go Back
                            </button>
                            <UpdateButton button-label="Update"
                                          :sending="sending"
                                          @click="acceptEdit" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </template>
</template>

<script setup>
    import { ref, computed, defineProps, defineEmits, watch, onMounted, nextTick, defineAsyncComponent } from 'vue';
    import { Tooltip } from 'bootstrap';
    import { modalDialog } from '../../js/ModalDialog.js';
    const UpdateButton = defineAsyncComponent(() => import('../UpdateButton.vue'));
    const ViewEditView = defineAsyncComponent(() => import('./impl/ViewEditView.vue'));
    const ViewEditEdit = defineAsyncComponent(() => import('./impl/ViewEditEdit.vue'));
    const ViewEditBlockTable = defineAsyncComponent(() => import('./impl/ViewEditBlockTable.vue'));

    const props = defineProps({
        modelValue: {
            type: Array,
            default: null
        },
        comparison: {
            type: Object,
            default: null
        },
        listType: {
            type: String,
            default: null
        },
        listClass: {
            type: String,
            default: null
        },
        layout: {
            type: Object,
            default: null
        },
        editModeOn: {
            type: Boolean,
            default: false
        },
        showDelete: {
            type: Boolean,
            default: false
        },
        showDeleteItem: {
            type: Boolean,
            default: false
        },
        showAddItem: {
            type: Boolean,
            default: false
        },
        sending: {
            type: Boolean,
            default: false
        },
        errorText: {
            type: String,
            default: null
        },
        inlineDisplay: {
            type: Boolean,
            default: false
        },
        tableOnPrint: {
            type: Boolean,
            default: false
        },
        printing: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['update:modelValue', 'cancelPressed', 'updatePressed', 'deletePressed']);

    onMounted(() => {
        new Tooltip(document.body, {
            selector: "[data-bs-toggle='tooltip']",
        });

        if (props.editModeOn) { // started with edit on
            openEditMode();
        }
    })

    const dialogId = computed(() => `editdialog-${props.layout.id}`);
    const data = computed(() => generateData(props.modelValue));
    const heldComparison = computed(() => props.comparison ? generateData(props.comparison) : generateEmptyComparison(props.modelValue));
    const printViewStyle = computed(() => calcViewClass(props.printing, props.tableOnPrint));
    const printPrintStyle = computed(() => props.printing ? "" : "only-print");
    const heldData = ref([]); // start empty

    function calcViewClass(tableOnPrint, printing) {
        if (tableOnPrint) {
            return printing ? "do-not-show" : "not-to-print";
        } else {
            return "";
        }
    }

    function generateData(baseData) {
        const calcedData = [];
        for (let m = 0; m < baseData.length; ++m) {
            let data = baseData[m];
            data.index = m;
            let thisRow = [];
            for (let r = 0; r < props.layout.rows.length; ++r) {
                let row = props.layout.rows[r];
                let colsArray = [];
                thisRow.push(colsArray);
                for (let c = 0; c < row.columns.length; ++c) {
                    let cell = row.columns[c];
                    colsArray.push(data[cell.attributeName]);
                }
            }
            calcedData.push(thisRow);
        }

        return calcedData;
    }

    function generateEmptyComparison(baseData) {
        const calcedData = [];
        for (let i = 0; i < baseData.length; ++i) {
            calcedData.push(null);
        }
        return calcedData;
    }

    function updatedData() {
        const update = [];

        for (let m = 0; m < heldData.value.length; ++m) {
            let data = heldData.value[m];
            let updateData = { ...props.modelValue[m] }; // start with whatever was passed in
            for (let r = 0; r < props.layout.rows.length; ++r) {
                let row = props.layout.rows[r];
                for (let c = 0; c < row.columns.length; ++c) {
                    let cell = row.columns[c];
                    updateData[cell.attributeName] = data[r][c]; // update the attributes
                }
            }
            update.push(updateData);
        }

        return update;
    }

    function olClass(listClass) {
        if (listClass) {
            return listClass;
        } else {
            return "";
        }
    }

    function cancelEdit() {
        heldData.value = generateData(props.modelValue); // reset the data
        emit('cancelPressed');
    }

    function deletePressed() {
        emit('deletePressed');
    }

    function acceptEdit() {
        let update = updatedData();
        emit('update:modelValue', update);
        // Make sure the model update has gone through
        nextTick(() => emit('updatePressed'));
    }

    function modelChanged(newModel, index) {
        heldData.value[index] = newModel;
    }

    function dialogSize(editSize) {
        if (editSize) {
            return editSize;
        } else {
            return "dialog-very-very-wide";
        }
    }

    function openEditMode() {
        // when we open edit mode, we set up the data to edit
        heldData.value = generateData(props.modelValue);

        modalDialog.openModal(dialogId.value);

        // Update tooltips, in case new items added on dialog show
        new Tooltip(document.body, {
            selector: "[data-bs-toggle='tooltip']",
        });
    }

    function closeEditMode() {
        modalDialog.closeModal(dialogId.value);
    }

    function deleteItem(index) {
        if ((index >= 0) && (index < heldData.value.length)) {
            heldData.value.splice(index, 1); // remove the item
        }
    }

    function addClicked() {
        let thisRow = [];
        for (let r = 0; r < props.layout.rows.length; ++r) {
            let row = props.layout.rows[r];
            let colsArray = [];
            thisRow.push(colsArray);
            for (let c = 0; c < row.columns.length; ++c) {
                colsArray.push(null);
            }
        }
        heldData.value.push(thisRow);
    }

    watch(() => props.editModeOn, (newEditMode, oldEditMode) => {
        if (newEditMode && !oldEditMode) {
            // give the system time to mount the dialog
            nextTick(() => openEditMode());
        } else if (!newEditMode && oldEditMode) {
            closeEditMode();
        }
    });

</script>

<style scoped>
    ol {
        padding-left: 0;
    }
</style>